import { useCallback, useEffect, useMemo, useState } from "react";
import { getToken, isInternalEmployee, useLogin } from "../../../authConfig";
import { useMsal } from "@azure/msal-react";
import { Conversation } from "../../../components/conversation";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
    Title,
    Text,
    Group,
    Button,
    LoadingOverlay,
    Alert,
    Stack,
    Paper,
    Divider,
    Grid,
    Card,
    ActionIcon,
    Drawer,
    Modal,
    useMantineTheme
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { MdArrowBack, MdRefresh, MdDeleteOutline, MdOpenInNew } from "react-icons/md";
import {
    getChatHistoryItemAdminApi,
    deleteChatHistoryItemAdminApi,
    getChatHistoryItemDatabricksTraceUrlApi,
    AdminChatHistoryItem,
    Source,
    ApiError
} from "../../../api";
import { SourceViewer } from "../../../components/sourceviewer";

export interface HistoryDetailsProps {}

const HistoryDetails = ({}: HistoryDetailsProps) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const client = useLogin ? useMsal().instance : undefined;
    const [chatHistoryData, setChatHistoryData] = useState<AdminChatHistoryItem | null>(null);
    const { historyId } = useParams<{ historyId: string }>();
    const [opened, { toggle }] = useDisclosure(false);
    const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [loadingTraceUrl, setLoadingTraceUrl] = useState(false);
    const [traceError, setTraceError] = useState<string | null>(null);
    const [selectedSource, setSelectedSource] = useState<Source | undefined>(undefined);
    const [selectedSourceAnchor, setSelectedSourceAnchor] = useState<string>();
    const theme = useMantineTheme();
    const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);
    const drawerWidth = useMemo(() => (isMobile ? "100%" : "80%"), [isMobile]);

    const loadHistoryData = async () => {
        if (!historyId) return;

        setLoading(true);
        setError(null);
        try {
            const idToken = client ? await getToken(client) : undefined;
            if (!idToken) {
                throw new Error("No idToken available for fetching history");
            }

            // Get conversation data using the existing chat history API
            const chatHistoryData = await getChatHistoryItemAdminApi(historyId, idToken);
            setChatHistoryData(chatHistoryData);
        } catch (error) {
            console.error("Failed to fetch history", error);
            setError(error instanceof Error ? error.message : "Failed to load history details");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!historyId) return;
        if (!chatHistoryData?.entra_oid) {
            setDeleteError("Cannot delete conversation: owner information is missing");
            return;
        }

        setDeleting(true);
        setDeleteError(null);
        try {
            const idToken = client ? await getToken(client) : undefined;
            if (!idToken) {
                throw new Error("No idToken available for deleting history");
            }

            await deleteChatHistoryItemAdminApi(historyId, chatHistoryData.entra_oid, idToken);
            navigate(`/administration/history/conversations?${searchParams.toString()}`);
        } catch (error) {
            console.error("Failed to delete conversation", error);
            setDeleteError(error instanceof Error ? error.message : "Failed to delete conversation");
        } finally {
            setDeleting(false);
        }
    };

    const handleViewDatabricksTraces = async () => {
        if (!historyId) return;

        setLoadingTraceUrl(true);
        setTraceError(null);
        try {
            const idToken = client ? await getToken(client) : undefined;
            if (!idToken) {
                throw new Error("No idToken available for fetching Databricks trace link");
            }

            const url = await getChatHistoryItemDatabricksTraceUrlApi(historyId, idToken);
            window.open(url, "_blank", "noopener,noreferrer");
        } catch (error) {
            console.error("Failed to fetch Databricks trace link", error);
            if (error instanceof ApiError && error.status === 404) {
                setTraceError("No Databricks traces found for this conversation");
            } else {
                setTraceError(error instanceof Error ? error.message : "Failed to fetch Databricks trace link");
            }
        } finally {
            setLoadingTraceUrl(false);
        }
    };

    useEffect(() => {
        loadHistoryData();
    }, [historyId, client]);

    const formatDate = (timestamp: string | number): string => {
        return new Date(timestamp).toLocaleString("en-US");
    };

    const onSourceClick = useCallback(
        async (source: Source, anchor: string) => {
            if (source.dataSource === "playbook") {
                window.open(source.url, "_blank");
            } else if (source.dataSource === "pm360") {
                window.open(source.url, "_blank");
            } else if (source.dataSource === "orbit" && client && !(await isInternalEmployee(client))) {
                window.open(source.url, "_blank");
            } else if (source.dataSource === "intellum") {
                window.open(source.url, "_blank");
            } else if (source.dataSource === "webpage") {
                window.open(source.url, "_blank");
            } else if (source.dataSource === "taxonomy_item") {
                window.open(source.url, "_blank");
            } else {
                toggle();
                setSelectedSource(source);
                setSelectedSourceAnchor(anchor);
            }
        },
        [client, toggle]
    );

    if (!historyId) {
        return (
            <Paper p="md">
                <Alert color="red" title="Error">
                    History ID is required to view conversation details.
                </Alert>
            </Paper>
        );
    }

    return (
        <Stack gap="md" pos="relative">
            <LoadingOverlay visible={loading} />

            <Stack gap="md">
                <Group justify="space-between" align="center">
                    <Group>
                        <ActionIcon variant="light" onClick={() => navigate(`/administration/history/conversations?${searchParams.toString()}`)} size="lg">
                            <MdArrowBack size={18} />
                        </ActionIcon>
                        <div>
                            <Title order={3}>{chatHistoryData?.username || "N/A"}</Title>
                            <Text size="sm" c="dimmed">
                                {chatHistoryData?.title || "Conversation Details"}
                            </Text>
                        </div>
                    </Group>
                    <Group gap="xs">
                        <Button variant="light" leftSection={<MdRefresh size={16} />} onClick={loadHistoryData} disabled={loading}>
                            Refresh
                        </Button>
                        <Button
                            variant="light"
                            leftSection={<MdOpenInNew size={16} />}
                            onClick={handleViewDatabricksTraces}
                            loading={loadingTraceUrl}
                            disabled={loading}
                        >
                            View Databricks Traces
                        </Button>
                        <Button color="red" variant="light" leftSection={<MdDeleteOutline size={16} />} onClick={openDeleteModal} disabled={loading}>
                            Delete Conversation
                        </Button>
                    </Group>
                </Group>

                {error && <Alert color="red">{error}</Alert>}
                {traceError && (
                    <Alert color="yellow" withCloseButton onClose={() => setTraceError(null)}>
                        {traceError}
                    </Alert>
                )}

                {chatHistoryData && (
                    <Paper p="md">
                        <Grid gap="lg">
                            <Grid.Col span={{ base: 12, md: 6 }}>
                                <Card withBorder radius="lg" shadow="sm">
                                    <Stack gap="sm">
                                        <Title order={5}>Conversation Information</Title>
                                        <Divider my="sm" />

                                        <Stack gap="xs">
                                            <Text fw={500}>Name</Text>
                                            <Text size="sm" c="dimmed">
                                                {chatHistoryData.name}
                                            </Text>
                                        </Stack>

                                        <Stack gap="xs">
                                            <Text fw={500}>Username</Text>
                                            <Text size="sm" c="dimmed">
                                                {chatHistoryData.username || "N/A"}
                                            </Text>
                                        </Stack>

                                        <Stack gap="xs">
                                            <Text fw={500}>Email</Text>
                                            <Text size="sm" c="dimmed">
                                                {chatHistoryData.email || "N/A"}
                                            </Text>
                                        </Stack>

                                        <Stack gap="xs">
                                            <Text fw={500}>User ID</Text>
                                            <Text size="sm" c="dimmed">
                                                {chatHistoryData.entra_oid || "N/A"}
                                            </Text>
                                        </Stack>

                                        <Stack gap="xs">
                                            <Text fw={500}>Date</Text>
                                            <Text size="sm" c="dimmed">
                                                {formatDate(chatHistoryData.timestamp)}
                                            </Text>
                                        </Stack>
                                    </Stack>
                                </Card>
                            </Grid.Col>

                            <Grid.Col span={{ base: 12, md: 6 }}>
                                <Card withBorder radius="lg" shadow="sm">
                                    <Stack gap="sm">
                                        <Title order={5}>Conversation Title</Title>
                                        <Divider my="sm" />

                                        <Text size="sm">{chatHistoryData.title}</Text>
                                    </Stack>
                                </Card>
                            </Grid.Col>
                        </Grid>

                        <Divider my="lg" />

                        <Card withBorder radius="lg" shadow="sm">
                            <Stack gap="sm">
                                <Title order={5}>Conversation History</Title>
                                <Divider my="sm" />

                                <Conversation
                                    hasError={false}
                                    showSttRequestIds
                                    isLoading={false}
                                    isStreaming={false}
                                    answers={chatHistoryData.answers}
                                    onSourceClick={onSourceClick}
                                    onFollowupQuestionClick={() => {}}
                                />
                            </Stack>
                        </Card>
                    </Paper>
                )}
            </Stack>
            <Drawer
                withOverlay={true}
                opened={opened}
                onClose={toggle}
                position="right"
                size={drawerWidth}
                title={selectedSource?.title}
                withCloseButton={true}
                transitionProps={{ transition: "fade-left", duration: 150, timingFunction: "linear" }}
                styles={{
                    body: {
                        height: "calc(100vh - 7rem)"
                    }
                }}
            >
                <SourceViewer source={selectedSource} anchor={selectedSourceAnchor} />
            </Drawer>
            <Modal opened={deleteModalOpened} onClose={deleting ? () => {} : closeDeleteModal} title="Delete Conversation" centered>
                <Stack gap="md">
                    <Text size="sm">
                        This will permanently delete this conversation, all feedback submitted on it, and its Databricks traces. This cannot be undone.
                    </Text>
                    {deleteError && <Alert color="red">{deleteError}</Alert>}
                    <Group justify="flex-end">
                        <Button variant="default" onClick={closeDeleteModal} disabled={deleting}>
                            Cancel
                        </Button>
                        <Button color="red" onClick={handleDelete} loading={deleting}>
                            Delete
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    );
};

export default HistoryDetails;
